import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArchiveRestore, BrushCleaning, Undo2 } from 'lucide-react';
import { runAudit } from '../api/audit';
import { fetchJournal, restoreEntries, runPurge } from '../api/purge';
import { ApiError } from '../api/client';
import { KpiCard } from '../components/KpiCard';
import { ProtocolBadge } from '../components/ProtocolBadge';
import { StepUpModal } from '../components/StepUpModal';
import { useAuth } from '../context/AuthContext';
import type {
  AuditFinding,
  AuditReport,
  AuditRuleInfo,
  AuditSeverity,
  AuditSubjectType,
  Env,
  JournalData,
  JournalEntry,
} from '../types';

const TABS: Env[] = ['prod', 'staging'];

const FAMILY_LABEL: Record<AuditRuleInfo['famille'], string> = {
  stations: 'Stations',
  identite: 'Identite des bornes',
  connectivite: 'Connectivite (OCPP / MQTT)',
  position: 'Position',
};
const FAMILY_ORDER: AuditRuleInfo['famille'][] = [
  'stations',
  'identite',
  'connectivite',
  'position',
];

const SEVERITY_LABEL: Record<AuditSeverity, string> = { bug: 'bug', verifier: 'a verifier' };
const SEVERITY_PILL: Record<AuditSeverity, string> = {
  bug: 'pill pill-red',
  verifier: 'pill pill-amber',
};

/** Cle stable d'un sujet (une borne peut etre signalee par plusieurs regles). */
function fkey(f: AuditFinding): string {
  return `${f.sujet.type}:${f.sujet.id}`;
}

const SUBJECT_WORD: Record<AuditSubjectType, string> = {
  device: 'borne',
  place: 'station',
  mqtt: 'appareil MQTT',
};
function subjectWord(type: AuditSubjectType): string {
  return SUBJECT_WORD[type];
}

function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Export local : le rapport est deja en memoire, inutile de repasser par l'API. */
function downloadCsv(report: AuditReport) {
  const byRule = new Map(report.rules.map((r) => [r.id, r]));
  const head = [
    'env',
    'severite',
    'regle',
    'type',
    'id',
    'numero_de_serie',
    'libelle',
    'protocole',
    'detail',
    'nettoyable',
  ];
  const lines = [head.join(';')];
  for (const f of report.findings) {
    lines.push(
      [
        report.env,
        SEVERITY_LABEL[f.severite],
        byRule.get(f.ruleId)?.label ?? f.ruleId,
        subjectWord(f.sujet.type),
        f.sujet.id,
        f.sujet.sn ?? '',
        f.sujet.label,
        f.protocole ?? '',
        f.detail,
        f.eligible === undefined
          ? ''
          : f.eligible
            ? 'oui'
            : `non (${(f.blockers ?? []).join(', ')})`,
      ]
        .map(csvCell)
        .join(';')
    );
  }
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${report.env}-${report.runAt.slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Une ligne de resultat
// ---------------------------------------------------------------------------

function FindingRow({
  finding,
  env,
  selecting,
  checked,
  onToggle,
}: {
  finding: AuditFinding;
  env: Env;
  selecting: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const { sujet, detail, eligible, blockers, protocole } = finding;
  // Seule une borne a une fiche ; un appareil MQTT orphelin n'existe pas dans `devices`.
  const to =
    sujet.type === 'device' && sujet.sn
      ? `/devices/${encodeURIComponent(sujet.sn)}?env=${env}`
      : null;
  const locked = selecting && eligible === false;
  return (
    <tr className={`tr-hover ${locked ? 'opacity-50' : ''}`}>
      {selecting && (
        <td className="td w-10">
          <input
            type="checkbox"
            checked={checked}
            disabled={eligible !== true}
            onChange={onToggle}
            aria-label={`Selectionner ${sujet.label}`}
            title={locked ? `Non nettoyable : ${(blockers ?? []).join(', ')}` : undefined}
          />
        </td>
      )}
      <td className="td font-medium t-strong">
        <span className="inline-flex items-center gap-2 flex-wrap">
          {to ? (
            <Link to={to} className="t-link">
              {sujet.label}
            </Link>
          ) : (
            sujet.label
          )}
          {sujet.type !== 'place' && <ProtocolBadge protocol={protocole} />}
        </span>
      </td>
      <td className="td num t-muted">
        {subjectWord(sujet.type)} #{sujet.id}
      </td>
      <td className="td t-muted">
        {detail}
        {locked && (
          <span className="block text-[11px] text-amber-300/90 mt-0.5">
            historique present : {(blockers ?? []).join(', ')} - a traiter depuis la fiche
          </span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Un groupe = une regle
// ---------------------------------------------------------------------------

function RuleGroup({
  rule,
  findings,
  env,
  canPurge,
  selecting,
  selected,
  onToggle,
  onToggleAll,
  onStartClean,
  onCancelClean,
  onDelete,
}: {
  rule: AuditRuleInfo;
  findings: AuditFinding[];
  env: Env;
  canPurge: boolean;
  selecting: boolean;
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: (keys: string[], on: boolean) => void;
  onStartClean: () => void;
  onCancelClean: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(rule.severite === 'bug');
  const clean = rule.count === 0;
  const expanded = (open || selecting) && !clean;

  const eligibleKeys = findings.filter((f) => f.eligible === true).map(fkey);
  const selectedHere = findings.filter((f) => selected.has(fkey(f))).length;
  const allOn = eligibleKeys.length > 0 && eligibleKeys.every((k) => selected.has(k));

  return (
    <div className="panel p-0 overflow-hidden">
      <div className="panel-header flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={() => !clean && !selecting && setOpen((o) => !o)}
          className="min-w-0 text-left flex-1 focus-visible:outline-none"
          aria-expanded={expanded}
          disabled={clean || selecting}
        >
          <span className="panel-title block">{rule.label}</span>
          <span className="block text-[12px] t-muted mt-0.5">{rule.description}</span>
        </button>
        <span className="flex items-center gap-2 shrink-0">
          {clean ? (
            <span className="pill pill-green">
              <span className="pill-dot" />
              conforme
            </span>
          ) : (
            <>
              <span className={SEVERITY_PILL[rule.severite]}>
                <span className="pill-dot" />
                {SEVERITY_LABEL[rule.severite]}
              </span>
              <span className="text-[13px] font-semibold t-strong num">{rule.count}</span>
              {canPurge && !selecting && (
                <button
                  type="button"
                  onClick={onStartClean}
                  className="btn-outline h-8 px-3 inline-flex items-center gap-1.5"
                  title={
                    eligibleKeys.length
                      ? 'Choisir les elements a supprimer'
                      : 'Aucun element nettoyable ici : ouvrir pour voir pourquoi'
                  }
                >
                  <BrushCleaning className="h-4 w-4" />
                  Nettoyer
                  {eligibleKeys.length < findings.length && (
                    <span className="t-faint num">
                      {eligibleKeys.length}/{findings.length}
                    </span>
                  )}
                </button>
              )}
            </>
          )}
        </span>
      </div>

      {expanded && (
        <>
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {selecting && (
                    <th className="th w-10">
                      <input
                        type="checkbox"
                        checked={allOn}
                        disabled={!eligibleKeys.length}
                        onChange={(e) => onToggleAll(eligibleKeys, e.target.checked)}
                        aria-label="Tout selectionner"
                        title="Tout cocher (elements nettoyables)"
                      />
                    </th>
                  )}
                  <th className="th">Sujet</th>
                  <th className="th">Reference</th>
                  <th className="th">Detail</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((f, i) => (
                  <FindingRow
                    key={`${f.ruleId}-${fkey(f)}-${i}`}
                    finding={f}
                    env={env}
                    selecting={selecting}
                    checked={selected.has(fkey(f))}
                    onToggle={() => onToggle(fkey(f))}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {selecting && (
            <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 border-t bd bg-inset">
              <span className="text-[13px] t-muted">
                <span className="t-strong num font-semibold">{selectedHere}</span> selectionne(s)
                sur {eligibleKeys.length} nettoyable(s)
                {findings.length > eligibleKeys.length && (
                  <>
                    {' '}
                    - {findings.length - eligibleKeys.length} avec historique (non selectionnable)
                  </>
                )}
              </span>
              <span className="flex gap-2">
                <button type="button" onClick={onCancelClean} className="btn-outline h-8 px-3">
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="btn-danger h-8 px-3 inline-flex items-center gap-1.5"
                  disabled={!selectedHere}
                >
                  <BrushCleaning className="h-4 w-4" />
                  Supprimer la selection ({selectedHere})
                </button>
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La corbeille
// ---------------------------------------------------------------------------

function JournalPanel({
  data,
  loading,
  selected,
  onToggle,
  onRestore,
}: {
  data: JournalData | null;
  loading: boolean;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onRestore: () => void;
}) {
  const entries = data?.entries ?? [];
  const restorable = entries.filter((e) => e.restorable);

  function status(e: JournalEntry): { text: string; cls: string } {
    if (e.restoredAt)
      return { text: `restauree le ${e.restoredAt.slice(0, 16)}`, cls: 'pill pill-green' };
    if (e.restorable) return { text: 'restaurable', cls: 'pill pill-sky' };
    return { text: 'expiree', cls: 'pill pill-gray' };
  }

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">Corbeille</h2>
      <div className="panel p-0 overflow-hidden">
        <div className="panel-header flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="panel-title block">Elements supprimes</span>
            <span className="block text-[12px] t-muted mt-0.5">
              Copie de chaque element supprime depuis l&apos;audit. « Restaurer » le re-insere tel
              qu&apos;il etait, tant que la fenetre de restauration n&apos;est pas depassee.
            </span>
          </div>
          {restorable.length > 0 && (
            <button
              type="button"
              onClick={onRestore}
              className="btn-outline h-8 px-3 inline-flex items-center gap-1.5 shrink-0"
              disabled={!selected.size}
            >
              <ArchiveRestore className="h-4 w-4" />
              Restaurer la selection ({selected.size})
            </button>
          )}
        </div>
        {loading && <div className="px-5 py-4 text-[13px] t-muted">Chargement...</div>}
        {!loading && data && !data.available && (
          <div className="px-5 py-4 text-[13px] t-muted">
            Nettoyage indisponible sur cet environnement (ecriture desactivee ou migration 009 non
            jouee).
          </div>
        )}
        {!loading && data?.available && entries.length === 0 && (
          <div className="px-5 py-4 text-[13px] t-muted">La corbeille est vide.</div>
        )}
        {!loading && data?.available && entries.length > 0 && (
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="th w-10" />
                  <th className="th">Element</th>
                  <th className="th">Reference</th>
                  <th className="th">Regle</th>
                  <th className="th">Supprime par</th>
                  <th className="th">Le</th>
                  <th className="th">Etat</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const s = status(e);
                  return (
                    <tr key={e.id} className="tr-hover">
                      <td className="td">
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          disabled={!e.restorable}
                          onChange={() => onToggle(e.id)}
                          aria-label={`Selectionner ${e.subjectLabel ?? e.id}`}
                        />
                      </td>
                      <td className="td font-medium t-strong">
                        {e.subjectLabel ?? `#${e.subjectId}`}
                      </td>
                      <td className="td num t-muted">
                        {subjectWord(e.subjectType)} #{e.subjectId}
                        {e.subjectSn ? ` - ${e.subjectSn}` : ''}
                      </td>
                      <td className="td t-muted">{e.ruleId ?? '-'}</td>
                      <td className="td t-muted">{e.deletedBy}</td>
                      <td className="td num t-muted">{e.deletedAt.slice(0, 16)}</td>
                      <td className="td">
                        <span className={s.cls}>
                          <span className="pill-dot" />
                          {s.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// La page
// ---------------------------------------------------------------------------

type Modal =
  | { kind: 'purge'; ruleId: string; items: AuditFinding[] }
  | { kind: 'restore'; ids: number[]; labels: string[] };

interface Toast {
  text: string;
  tone: 'green' | 'amber' | 'red';
  /** Entrees du journal a restaurer si l'utilisateur clique « Annuler ». */
  undoIds?: number[];
}

export function AuditPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState<Env>('prod');
  const [report, setReport] = useState<AuditReport | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nettoyage : la regle en cours de selection + les sujets coches.
  const [cleaning, setCleaning] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<Modal | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  // Corbeille (admin).
  const [journal, setJournal] = useState<JournalData | null>(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalSel, setJournalSel] = useState<Set<number>>(new Set());

  function launch(env: Env) {
    setRunning(true);
    setError(null);
    setCleaning(null);
    setSelected(new Set());
    runAudit(env)
      .then((r) => setReport(r.data))
      .catch((e) => {
        setReport(null);
        setError((e as Error).message);
      })
      .finally(() => setRunning(false));
  }

  function loadJournal(env: Env) {
    if (!isAdmin) return;
    setJournalLoading(true);
    fetchJournal(env)
      .then((r) => setJournal(r.data))
      .catch(() => setJournal(null))
      .finally(() => setJournalLoading(false));
    setJournalSel(new Set());
  }

  useEffect(() => {
    loadJournal(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  function selectEnv(env: Env) {
    if (env === tab) return;
    setTab(env);
    setReport(null); // un rapport appartient a son environnement : on ne le garde pas
    setError(null);
    setCleaning(null);
    setSelected(new Set());
    setToast(null);
  }

  // Un rapport n'est affiche que s'il correspond a l'onglet courant.
  const shown = report && report.env === tab ? report : null;
  const canPurge = isAdmin && !!shown?.purgeAvailable;

  function toggle(key: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }
  function toggleAll(keys: string[], on: boolean) {
    setSelected((s) => {
      const n = new Set(s);
      for (const k of keys) {
        if (on) n.add(k);
        else n.delete(k);
      }
      return n;
    });
  }

  function openPurge(ruleId: string) {
    if (!shown) return;
    const items = shown.findings.filter((f) => f.ruleId === ruleId && selected.has(fkey(f)));
    if (items.length) setModal({ kind: 'purge', ruleId, items });
  }

  async function doPurge(m: Extract<Modal, { kind: 'purge' }>) {
    const r = await runPurge(tab, {
      ruleId: m.ruleId,
      devices: m.items.filter((f) => f.sujet.type === 'device').map((f) => f.sujet.id),
      places: m.items.filter((f) => f.sujet.type === 'place').map((f) => f.sujet.id),
      mqtt: m.items.filter((f) => f.sujet.type === 'mqtt').map((f) => f.sujet.id),
      confirmCount: m.items.length,
    });
    const { results, done, failed } = r.data;
    const undoIds = results.filter((x) => x.ok && x.journalId).map((x) => x.journalId as number);
    const failures = results.filter((x) => !x.ok).map((x) => `${x.label} : ${x.error}`);
    setModal(null);
    setCleaning(null);
    setSelected(new Set());
    setToast({
      text:
        `${done} element(s) supprime(s) de ${tab}` +
        (failed ? ` - ${failed} echec(s) : ${failures.join(' ; ')}` : ''),
      tone: failed ? (done ? 'amber' : 'red') : 'green',
      undoIds: undoIds.length ? undoIds : undefined,
    });
    launch(tab);
    loadJournal(tab);
  }

  async function doRestore(ids: number[]) {
    const r = await restoreEntries(tab, ids);
    const { results, done, failed } = r.data;
    const failures = results.filter((x) => !x.ok).map((x) => `${x.label} : ${x.error}`);
    setModal(null);
    setToast({
      text:
        `${done} element(s) restaure(s) sur ${tab}` +
        (failed ? ` - ${failed} echec(s) : ${failures.join(' ; ')}` : ''),
      tone: failed ? (done ? 'amber' : 'red') : 'green',
    });
    launch(tab);
    loadJournal(tab);
  }

  /** « Annuler » depuis le bandeau : la reconnexion est en general encore valable, sinon on repasse par la modale. */
  async function undo(ids: number[]) {
    try {
      await doRestore(ids);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STEP_UP_REQUIRED') {
        setModal({ kind: 'restore', ids, labels: [] });
      } else {
        setToast({ text: (e as Error).message, tone: 'red' });
      }
    }
  }

  function openRestore() {
    const ids = [...journalSel];
    const labels = (journal?.entries ?? [])
      .filter((e) => journalSel.has(e.id))
      .map((e) => e.subjectLabel ?? `#${e.subjectId}`);
    if (ids.length) setModal({ kind: 'restore', ids, labels });
  }

  const toneCls = { green: 'note-green', amber: 'note-amber', red: 'note-red' } as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Audit de coherence</h1>
          <p className="page-desc">
            Controle en lecture seule de la flotte d&apos;un environnement. Rien n&apos;est modifie
            sans votre selection explicite.
          </p>
        </div>
        <div className="flex gap-2">
          {shown && (
            <button type="button" onClick={() => downloadCsv(shown)} className="btn-outline">
              Export CSV
            </button>
          )}
          <button type="button" onClick={() => launch(tab)} className="btn" disabled={running}>
            {running ? 'Audit en cours...' : shown ? 'Relancer l’audit' : `Lancer l’audit ${tab}`}
          </button>
        </div>
      </div>

      {/* prod et staging sont deux bases independantes : un audit par environnement. */}
      <div className="tabs">
        {TABS.map((e) => {
          const active = tab === e;
          return (
            <button
              key={e}
              type="button"
              onClick={() => selectEnv(e)}
              className={`tab inline-flex items-center gap-1.5 ${active ? 'tab-active' : ''}`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${e === 'prod' ? 'bg-rose-400' : 'bg-cyan-400'}`}
              />
              {e}
            </button>
          );
        })}
      </div>

      {toast && (
        <div
          className={`text-[13px] rounded-md border ${toneCls[toast.tone]} px-3 py-2 flex items-center justify-between gap-3 flex-wrap`}
        >
          <span>{toast.text}</span>
          <span className="flex items-center gap-2">
            {toast.undoIds && (
              <button
                type="button"
                onClick={() => undo(toast.undoIds!)}
                className="btn-outline h-7 px-2.5 inline-flex items-center gap-1.5"
              >
                <Undo2 className="h-3.5 w-3.5" />
                Annuler
              </button>
            )}
            <button type="button" onClick={() => setToast(null)} className="btn-ghost h-7 px-2">
              Fermer
            </button>
          </span>
        </div>
      )}

      {error && <div className="text-[13px] rounded-md border note-red px-3 py-2">{error}</div>}

      {!shown && !error && (
        <div className="panel empty-state">
          <p className="text-[13px] t-muted max-w-md">
            {running
              ? `Audit de ${tab} en cours...`
              : `Aucun audit lance pour ${tab}. Le controle parcourt les bornes et les stations de cet environnement et liste les incoherences.`}
          </p>
        </div>
      )}

      {shown && (
        <>
          <div className="stat-strip sm:grid-cols-4">
            <KpiCard
              label="Bornes analysees"
              value={shown.devices}
              hint={`${shown.byProtocol.ocpp} OCPP - ${shown.byProtocol.mqtt} MQTT - ${shown.byProtocol.both} les deux - ${shown.byProtocol.aucun} jamais connectee(s)${shown.mqttOrphans ? ` - ${shown.mqttOrphans} MQTT sans fiche` : ''}`}
            />
            <KpiCard label="Bugs" value={shown.bugs} hint="a corriger" />
            <KpiCard label="A verifier" value={shown.verifier} hint="controle humain" />
            <KpiCard
              label="Audit"
              value={`${shown.durationMs} ms`}
              hint={new Date(shown.runAt).toLocaleString('fr-FR')}
            />
          </div>

          {shown.bugs === 0 && shown.verifier === 0 && (
            <div className="text-[13px] rounded-md border note-green px-3 py-2">
              Aucune incoherence detectee sur {shown.env}.
            </div>
          )}

          {isAdmin && !shown.purgeAvailable && shown.bugs + shown.verifier > 0 && (
            <div className="text-[13px] rounded-md border note-amber px-3 py-2">
              Le nettoyage n&apos;est pas disponible sur {shown.env} : ecriture desactivee ou
              migration 009 (journal de restauration) non jouee.
            </div>
          )}

          {FAMILY_ORDER.map((famille) => {
            const rules = shown.rules.filter((r) => r.famille === famille);
            if (!rules.length) return null;
            return (
              <section key={famille} className="space-y-3">
                <h2 className="eyebrow">{FAMILY_LABEL[famille]}</h2>
                {rules.map((rule) => (
                  <RuleGroup
                    key={rule.id}
                    rule={rule}
                    findings={shown.findings.filter((f) => f.ruleId === rule.id)}
                    env={shown.env}
                    canPurge={canPurge}
                    selecting={cleaning === rule.id}
                    selected={selected}
                    onToggle={toggle}
                    onToggleAll={toggleAll}
                    onStartClean={() => {
                      setCleaning(rule.id);
                      setSelected(new Set());
                    }}
                    onCancelClean={() => {
                      setCleaning(null);
                      setSelected(new Set());
                    }}
                    onDelete={() => openPurge(rule.id)}
                  />
                ))}
              </section>
            );
          })}
        </>
      )}

      {isAdmin && (
        <JournalPanel
          data={journal}
          loading={journalLoading}
          selected={journalSel}
          onToggle={(id) =>
            setJournalSel((s) => {
              const n = new Set(s);
              if (n.has(id)) n.delete(id);
              else n.add(id);
              return n;
            })
          }
          onRestore={openRestore}
        />
      )}

      {modal?.kind === 'purge' && (
        <StepUpModal
          env={tab}
          title="Supprimer de la base"
          count={modal.items.length}
          confirmLabel={`Supprimer ${modal.items.length} element(s)`}
          onConfirm={() => doPurge(modal)}
          onClose={() => setModal(null)}
        >
          <div className="space-y-3">
            <p className="text-[13px] t-body">
              Ces elements vont etre{' '}
              <strong className="t-strong">supprimes de la base {tab}</strong>. Ils disparaitront de
              toutes les applications qui la lisent. Une copie est gardee dans la corbeille
              ci-dessous : vous pourrez les restaurer pendant la fenetre de restauration (30 jours
              par defaut).
            </p>
            <ul className="text-[13px] t-muted space-y-0.5 max-h-40 overflow-y-auto">
              {modal.items.slice(0, 12).map((f) => (
                <li key={fkey(f)}>
                  <span className="t-strong">{f.sujet.label}</span>{' '}
                  <span className="t-faint">
                    ({subjectWord(f.sujet.type)} #{f.sujet.id})
                  </span>
                </li>
              ))}
              {modal.items.length > 12 && <li>... et {modal.items.length - 12} autre(s)</li>}
            </ul>
          </div>
        </StepUpModal>
      )}

      {modal?.kind === 'restore' && (
        <StepUpModal
          env={tab}
          title="Restaurer depuis la corbeille"
          count={modal.ids.length}
          confirmLabel={`Restaurer ${modal.ids.length} element(s)`}
          danger={false}
          onConfirm={() => doRestore(modal.ids)}
          onClose={() => setModal(null)}
        >
          <div className="space-y-3">
            <p className="text-[13px] t-body">
              Les elements sont re-inseres dans la base {tab} tels qu&apos;ils etaient au moment de
              leur suppression.
            </p>
            {modal.labels.length > 0 && (
              <ul className="text-[13px] t-muted space-y-0.5 max-h-40 overflow-y-auto">
                {modal.labels.slice(0, 12).map((l, i) => (
                  <li key={`${l}-${i}`} className="t-strong">
                    {l}
                  </li>
                ))}
                {modal.labels.length > 12 && <li>... et {modal.labels.length - 12} autre(s)</li>}
              </ul>
            )}
          </div>
        </StepUpModal>
      )}
    </div>
  );
}
